/**
 * ==========================================================
 * ET • Poll (v1.3)
 * - Idempotent init guard (prevents double-binding + double poll_view)
 * - Fires poll_view when poll is actually visible (IntersectionObserver)
 * - Tracks vote lifecycle: poll_vote_attempt, poll_vote, poll_vote_error
 * - Sends poll_vote only after backend confirms persistence
 * - Restores UI state if vote request fails so user can retry
 * ==========================================================
 */
(function () {
  "use strict";

  const VOTE_ENDPOINT = "/apps/shop-app/api/proxy/vote";
  const VIEW_THRESHOLD = 0.35;

  function fireGa4(eventName, params) {
    if (typeof window.gtag === "function") {
      window.gtag("event", eventName, params || {});
    }
  }

  function buildGaPayload(pollId, pollHandle, pollType, resultsMode, pollInstance, extra) {
    return Object.assign(
      {
        poll_id: pollId,
        poll_handle: pollHandle,
        poll_type: pollType,
        poll_instance: pollInstance,
        results_mode: resultsMode,
        content_type: "article",
        store: "ET",
      },
      extra || {}
    );
  }

  function markButtonState(buttons, selectedBtn, isLocked) {
    buttons.forEach((btn) => {
      const isSelected = btn === selectedBtn;
      btn.classList.toggle("is-selected", isSelected && isLocked);
      btn.classList.toggle("is-dim", !isSelected && isLocked);
      btn.disabled = isLocked;
      btn.setAttribute("aria-disabled", isLocked ? "true" : "false");
      btn.setAttribute("aria-pressed", isSelected && isLocked ? "true" : "false");
    });
  }

  function fireViewWhenVisible(pollEl, gaPayload) {
    if (!pollEl || pollEl.dataset.etPollViewSent === "1") return;

    const fireView = function () {
      if (pollEl.dataset.etPollViewSent === "1") return;
      pollEl.dataset.etPollViewSent = "1";
      fireGa4("poll_view", gaPayload);
    };

    if (typeof window.IntersectionObserver !== "function") {
      fireView();
      return;
    }

    const observer = new IntersectionObserver(
      function (entries) {
        for (let i = 0; i < entries.length; i += 1) {
          if (entries[i].isIntersecting) {
            fireView();
            observer.disconnect();
            break;
          }
        }
      },
      { threshold: VIEW_THRESHOLD }
    );

    observer.observe(pollEl);
  }

  function renderResults(resultsContainer, data) {
    if (!resultsContainer || !data) return;

    const totalVotes = Number(data.totalVotes || 0);
    const results = Array.isArray(data.results) ? data.results : [];

    resultsContainer.innerHTML = "";

    const header = document.createElement("h3");
    header.textContent = "Total Votes: " + totalVotes;
    resultsContainer.appendChild(header);

    results.forEach((result) => {
      const answer = String(result.answer || "");
      const count = Number(result.count || 0);
      const percentage = Number(result.percentage || 0);
      const safePercentage = Math.max(0, Math.min(100, percentage));

      const resultItem = document.createElement("div");
      resultItem.className = "poll-result-item";

      const label = document.createElement("div");
      label.className = "result-label";

      const answerEl = document.createElement("span");
      answerEl.className = "answer";
      answerEl.textContent = answer;

      const countEl = document.createElement("span");
      countEl.className = "count";
      countEl.textContent = count + " (" + safePercentage + "%)";

      label.appendChild(answerEl);
      label.appendChild(countEl);

      const barContainer = document.createElement("div");
      barContainer.className = "result-bar-container";

      const bar = document.createElement("div");
      bar.className = "result-bar";
      bar.style.width = safePercentage + "%";

      barContainer.appendChild(bar);
      resultItem.appendChild(label);
      resultItem.appendChild(barContainer);
      resultsContainer.appendChild(resultItem);
    });
  }

  function initPoll(pollEl) {
    if (!pollEl) return;

    // --- Init guard: prevents duplicate listeners / duplicate poll_view ---
    if (pollEl.dataset.etPollInit === "1") return;
    pollEl.dataset.etPollInit = "1";

    const pollId = pollEl.getAttribute("data-poll-id") || "";
    const pollHandle = pollEl.getAttribute("data-poll-handle") || pollId;
    const pollInstance = pollEl.getAttribute("data-poll-instance") || "";
    const pollType = pollEl.getAttribute("data-poll-type") || "";
    const resultsMode = pollEl.getAttribute("data-results-mode") || "none";

    const buttons = Array.from(pollEl.querySelectorAll(".et-poll__btn"));
    const noteEl = pollEl.querySelector(".et-poll__note");
    const resultsContainer = pollEl.querySelector(".poll-results");
    const noteAfterVote = noteEl ? noteEl.getAttribute("data-note-after-vote") : "";
    const fallbackSuccessNote = "Thanks - your answer helps us improve future guides.";
    const fallbackErrorNote = "We couldn't save your vote. Please try again.";

    markButtonState(buttons, null, false);

    const baseGaPayload = buildGaPayload(
      pollId,
      pollHandle,
      pollType,
      resultsMode,
      pollInstance
    );

    fireViewWhenVisible(pollEl, baseGaPayload);

    let voted = false;

    buttons.forEach((btn, idx) => {
      btn.addEventListener("click", async function () {
        if (voted) return;
        voted = true;

        const answer = btn.getAttribute("data-answer") || "";
        const voteGaPayload = buildGaPayload(
          pollId,
          pollHandle,
          pollType,
          resultsMode,
          pollInstance,
          {
            answer: answer,
            answer_index: idx + 1,
          }
        );

        markButtonState(buttons, btn, true);

        fireGa4("poll_vote_attempt", voteGaPayload);

        if (noteEl) {
          noteEl.textContent = "";
        }

        try {
          const response = await fetch(VOTE_ENDPOINT, {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: new URLSearchParams({
              poll_id: pollId,
              poll_handle: pollHandle,
              answer: answer,
            }),
          });

          if (!response.ok) {
            throw new Error("http_" + response.status);
          }

          const data = await response.json();

          fireGa4(
            "poll_vote",
            buildGaPayload(pollId, pollHandle, pollType, resultsMode, pollInstance, {
              answer: answer,
              answer_index: idx + 1,
              vote_status: "success",
              total_votes: Number(data && data.totalVotes ? data.totalVotes : 0),
            })
          );

          if (resultsMode === "after_vote" && resultsContainer) {
            renderResults(resultsContainer, data);
          }

          if (noteEl) {
            noteEl.textContent = noteAfterVote ? noteAfterVote : fallbackSuccessNote;
          }
        } catch (error) {
          voted = false;
          markButtonState(buttons, null, false);

          if (noteEl) {
            noteEl.textContent = fallbackErrorNote;
          }

          fireGa4(
            "poll_vote_error",
            buildGaPayload(pollId, pollHandle, pollType, resultsMode, pollInstance, {
              answer: answer,
              answer_index: idx + 1,
              vote_status: "error",
            })
          );
        }
      });
    });
  }

  function boot() {
    document.querySelectorAll("[data-et-poll]").forEach(initPoll);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
